import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Col, Row, Spin } from 'antd';
import { useTranslation } from 'react-i18next';

import {
	setDeliveryModalIsOpen,
	setDeliveryModalData,
} from 'store/slices/layoutSlice';
import { calcBasedOnYaDelivery } from 'store/slices/cartSlice';
import HeadingTitle from 'components/HeadingTitle';
import useBreakpoint from 'hooks/useBreakpoint';
import { formatCurrency } from 'helpers/formatter';
import { geoObjectDisplayName } from 'helpers/suggest-provider';
import { parseProductWeightToKg } from 'helpers/product';
import {
	addNalchikZoneToYandexMap,
	getNalchikFixedShippingCost,
	isInsideNalchikDeliveryZone,
	NALCHIK_STORE_ID,
} from 'helpers/nalchikDeliveryZone';

const DeliveryCalculate = () => {
	const { t } = useTranslation();
	const mapRef = useRef();
	const suggestRef = useRef();
	const mapIsCreated = useRef();
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch();
	const { data: store } = useSelector(state => state.layout.mainInfo);
	const { data: cartData } = useSelector(state => state.cart.cartProducts);
	const { isDesktop } = useBreakpoint();

	const { costAddress } = useSelector(state => state.layout.deliveryModalData);

	const handleSetData = ({ address, costAddress }) => {
		setError('');
		dispatch(
			setDeliveryModalData({
				address,
				costAddress,
			}),
		);
		setLoading(false);
	};

	const setDataCb = address => {
		return rawCost => {
			const n =
				rawCost === null || rawCost === undefined ? NaN : Number(rawCost);
			if (!Number.isFinite(n) || n <= 0) {
				handleError(t('delivery.calculationFailed'));
				return;
			}
			handleSetData({ address, costAddress: n });
		};
	};

	const handleSubmitData = () => {
		dispatch(setDeliveryModalIsOpen(false));
	};

	const handleError = text => {
		setError(text);
		setLoading(false);
	};

	useEffect(() => {
		if (
			!store?.store_coord ||
			typeof store.store_coord !== 'string' ||
			!store.store_coord.includes(',')
		) {
			return;
		}
		const coordStr = store.store_coord;

		const handleLoad = () => {
			const ymaps = window.ymaps;
			const coordsParts = coordStr.split(',');
			const storeCoords = [coordsParts[1].trim(), coordsParts[0].trim()];
			console.log(
				'🏪 Координаты магазина (store.store_coord):',
				store.store_coord,
			);
			console.log('🏪 Parsed координаты [широта, долгота]:', storeCoords);
			const isNalchik = Number(store.store_id) === NALCHIK_STORE_ID;
			let lastRoute = null;

			const initMap = () => {
				const myMap = new ymaps.Map(mapRef.current, {
					center: storeCoords,
					zoom: 11,
					controls: [],
					behaviors: [],
				});

				if (isNalchik) {
					addNalchikZoneToYandexMap(ymaps, myMap);
				}

				const suggestView = new ymaps.SuggestView(suggestRef.current, {
					results: 10,
				});
				suggestView.events.add('select', function (e) {
					setError('');
					setLoading(true);
					ymaps.geocode(e.get('item').value).then(function (res) {
						const geoObject = res.geoObjects.get(0);
						const coords = geoObject.geometry.getCoordinates();
						console.log('📍 Координаты выбранного адреса:', coords);
						const address_meta = geoObject.properties.get(
							'metaDataProperty.GeocoderMetaData',
						);
						const house = address_meta.Address.Components.find(function (
							it,
							i,
						) {
							return it.kind === 'house';
						});

						if (lastRoute) myMap.geoObjects.remove(lastRoute);
						ymaps
							.route([storeCoords, coords], {
								mapStateAutoApply: true,
							})
							.then(function async(route) {
								const way = route.getPaths().get(0);
								const distanceKm = (way.getLength() / 1000).toFixed(1);
								console.log(
									'🚗 Лимит доставки:',
									store.limit_distance_shipping,
									'км',
								);
								console.log('📍 Расстояние по маршруту:', distanceKm, 'км');
								console.log(
									'✅ Доставка возможна?',
									+store.limit_distance_shipping >= +distanceKm,
								);
								if (+store.limit_distance_shipping >= +distanceKm) {
									if (
										address_meta.precision === 'exact' ||
										(house && house.name)
									) {
										lastRoute = route;
										myMap.geoObjects.add(lastRoute);
										const displayName = geoObjectDisplayName(geoObject);
										console.log(
											'🏙️ Текущий город (store.current_city):',
											store.current_city,
										);
										const rawKg =
											cartData.products?.reduce((sum, item) => {
												const kg = parseProductWeightToKg(item.weight);
												const quantity = parseInt(item.quantity, 10) || 1;
												return sum + kg * quantity;
											}, 0) ?? 0;
										const cartWeight =
											Number.isFinite(rawKg) && rawKg > 0 ? rawKg : 1;
										const cb = setDataCb(displayName);

										if (
											isNalchik &&
											isInsideNalchikDeliveryZone(coords)
										) {
											cb(getNalchikFixedShippingCost(store));
										} else {
											calcBasedOnYaDelivery(
												storeCoords,
												coords,
												cartWeight,
												cb,
											);
										}
									} else {
										handleError(t('delivery.fullAddressRequired'));
									}
								} else {
									handleError(
										`${t('delivery.deliveryImpossible')}\n${t('delivery.maxDistance', { distance: store.limit_distance_shipping })}\n\n${t('delivery.checkCity')}\n${t('delivery.wrongCity')}`,
									);
								}
							});
					});
				});
			};

			ymaps.ready(initMap);
			mapIsCreated.current = true;
		};

		if (!mapIsCreated.current) handleLoad();
	}, [store, cartData, dispatch]);

	const showAboveMap =
		!isDesktop &&
		costAddress !== undefined &&
		costAddress !== null &&
		costAddress !== '';

	return (
		<>
			<Row justify='space-between'>
				<Col xs={24} lg={12}>
					<div className={`${isDesktop ? 'pr-30' : ''}`}>
						<HeadingTitle title={t('delivery.calculateCost')} level={4} />
						<div className='mb-30 mt-30'>
							<div>{t('delivery.enterFullAddress')}</div>
							<input
								className='rn-input'
								ref={suggestRef}
								defaultValue={
									store.current_city?.label
										? `${store.current_city.label}, `
										: ''
								}
							/>
						</div>

						{loading ? <Spin className='mb-30' /> : ''}

						{error ? (
							<div className='mb-30'>
								<Alert
									message={error}
									type='error'
									showIcon
									className='mb-15'
								/>
								<p className='delivery-error-hint'>
									{t('delivery.tryOtherAddress')}
								</p>
								<Button
									type='default'
									onClick={() => {
										setError('');
										if (suggestRef.current) {
											suggestRef.current.value = store?.current_city?.label
												? `${store.current_city.label}, `
												: '';
										}
									}}
									className='mr-10'
								>
									{t('delivery.tryAgain')}
								</Button>
								<Button type='link' onClick={() => window.location.reload()}>
									{t('delivery.reloadPage')}
								</Button>
							</div>
						) : isDesktop &&
						  costAddress !== undefined &&
						  costAddress !== null &&
						  costAddress !== '' ? (
							<>
								<Alert
									message={
										<HeadingTitle
											title={`${t('delivery.deliveryCost')} ${formatCurrency(
												costAddress,
											)}`}
											level={5}
											style={{ margin: 0 }}
										/>
									}
									type='success'
									className='mb-30'
								/>
								<div className='mb-30'>
									<Button
										type='primary'
										block
										size='large'
										onClick={handleSubmitData}
									>
										{t('delivery.deliverHere')}
									</Button>
								</div>
							</>
						) : null}
					</div>
				</Col>
				{showAboveMap ? (
					<Col xs={24}>
						<Alert
							message={
								<HeadingTitle
									title={`${t('delivery.deliveryCost')} ${formatCurrency(costAddress)}`}
									level={5}
									style={{ margin: 0 }}
								/>
							}
							type='success'
							className='mb-15'
						/>
						<Button
							type='primary'
							block
							size='large'
							onClick={handleSubmitData}
							className='mb-15'
						>
							{t('delivery.accept')}
						</Button>
					</Col>
				) : null}
				<Col xs={24} lg={12}>
					<div className='rn-map' ref={mapRef} />
				</Col>
			</Row>
		</>
	);
};

export default DeliveryCalculate;
